<template>
  <div class="additional-cost-table">
    <div class="ml-auto d-flex justify-content-end">
      <McButton
        v-if="selectedIds.length > 0 && canEditFields"
        variant="secondary-red"
        class="mx-2"
        id="mc-delete-btn"
        :text="$t('global.delete')"
        icon="icon-outline_trash"
        @click="$refs['mc-modal-delete'].show()"
      />

      <McButton
        v-if="canEditFields"
        variant="primary"
        _disabled="disabledGlobal"
        text="Tambah"
        icon="icon-fill_plus"
        @click="handleAdd"
      />
    </div>

    <FormulateForm
      name="editable-table-form"
      ref="formRef"
      :errors="formErrors"
      @submit="handleSubmit"
      @failed-validation="handleFailedValidation"
    >
      <div class="table-responsive my-3 border border-1 mc-table-overflow">
        <b-table
          :busy="isBusy"
          :fields="computedFields"
          :items="items"
          class="editable-table"
          show-empty
        >
          <!-- Loading -->
          <template #table-busy>
            <div class="__busy">
              <b-spinner class="align-middle mb-2"></b-spinner>
              <strong>Loading...</strong>
            </div>
          </template>

          <!-- Empty Result -->
          <template #empty>
            <div class="text-center my-4">Tidak ada data</div>
          </template>

          <!-- Slot: Header Checkbox -->
          <template #head(checkbox)>
            <div class="form-check" style="width: 10px">
              <b-form-checkbox
                @change="handleSelectAll"
                :checked="selectedAll"
                :disabled="!canEditFields"
              ></b-form-checkbox>
            </div>
          </template>

          <!-- Slot: Cell Checkbox -->
          <template #cell(checkbox)="data">
            <div class="form-check">
              <b-form-checkbox
                @change="handleSelect($event, data)"
                :checked="isSelected(data.item.id)"
                :disabled="!canEditFields"
              ></b-form-checkbox>
            </div>
          </template>

          <!-- Slot: All Cells -->
          <template #cell()="{ index, field, value, item }">
            <template v-if="field.isEditable && isEditing(index)">
              <FormulateInput
                v-bind="field"
                :label="null"
                :type="field.type || 'text'"
                :value="getInitialValue(item, field)"
                :disabled="isSubmitting || field.disabled"
                :initialObject="field.getInitialObject?.(item)"
                size="sm"
                validation-behavior="live"
                error-behavior="live"
                :ref="`${field.name}-ref`"
              />
            </template>

            <template v-else>
              {{ transformCellValue(value) }}
            </template>
          </template>

          <!-- Slot: Action Column (Last Column) -->
          <template #cell(action)="data">
            <div>
              <template v-if="isEditing(data.index)">
                <McButton
                  variant="outline"
                  class="__accept-button btn"
                  type="submit"
                  icon="icon-fill_checkmark"
                  :text="$t('global.save')"
                />

                <McButton
                  type="button"
                  variant="outline"
                  class="__cancel-button btn"
                  @click.prevent="handleEditCancel"
                  icon="icon-outline_close"
                  :text="$t('global.cancel')"
                />
              </template>

              <template v-else>
                <McButton
                  v-if="canEditFields"
                  variant="outline"
                  type="button"
                  :class="{
                    '__edit-button btn': true,
                    __disabled: disabled || !isEditing(-1) || isBusy,
                  }"
                  @click.prevent="handleEditStart(data)"
                  icon="icon-outline_edit"
                  :text="$t('global.edit')"
                />
              </template>
            </div>
          </template>
        </b-table>
      </div>
    </FormulateForm>

    <b-modal
      class="mc-modal"
      id="mc-modal-delete"
      ref="mc-modal-delete"
      title="Hapus"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>
          Apakah Anda yakin ingin menghapus <strong>{{ selectedIds.length }}</strong> data berikut ?
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.cancel')"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.delete')" @click="handleDelete" />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { isEmpty } from 'lodash'
import McButton from '@/components/McButton'

import * as AdditionalCost from '@/services/rest-api/lib/contract/additional-cost'

import { EventBus } from '../../lib/event-bus.js'
import additionalCostTableListFields from '../../lib/fields/additional-cost-table-list-fields.js'
import {
  transformAdditionalCostUpdateRequest,
  transformAdditionalCostStoreRequest,
  transformAdditionalCostEmitPayload,
} from '../../lib/helpers/transform-data.js'

export default {
  components: {
    McButton,
  },
  props: {
    additionalCosts: {},
    isAddingRates: {},
    prefixUrl: {},
    canEditFields: {},
  },
  data() {
    return {
      isBusy: true,
      items: [],

      disabled: false,
      editingRow: -1,

      selectedIdsMap: {},

      isSubmitting: false,
      isAdding: false,

      formValues: {},
      formErrors: {},
    }
  },
  computed: {
    apiClient() {
      return {
        get: async () => await AdditionalCost.get(this.prefixUrl),
        store: async (...args) => await AdditionalCost.store(this.prefixUrl, ...args),
        update: async (...args) => await AdditionalCost.update(this.prefixUrl, ...args),
        delete: async (...args) => await AdditionalCost.deleteMany(this.prefixUrl, ...args),
      }
    },
    fields() {
      return additionalCostTableListFields
    },
    computedFields() {
      return [
        {
          key: 'checkbox',
          label: '',
        },
        ...this.fields,
        {
          key: 'action',
          label: '',
          thClass: 'action-header',
          tdClass: 'action-cell',
        },
      ]
    },
    selectedIds() {
      return Object.keys(this.selectedIdsMap).filter((id) => this.selectedIdsMap[id])
    },
    selectedAll() {
      if (this.items.length == 0) return false

      return this.selectedIds.length === this.items.length
    },
  },
  mounted() {
    this.items = this.additionalCosts
    this.isBusy = false
  },
  methods: {
    // Core
    async getData() {
      if (this.isAddingRates) {
        await this.$nextTick()
        this.items = this.additionalCosts
        this.isBusy = false
        return
      }

      this.isBusy = true
      const res = await this.apiClient.get()

      if (!res) {
        this.$mcToast.error('Terjadi kesalahan')
        this.isBusy = false
        return
      }

      this.items = res.data
      this.isBusy = false
    },
    // Add
    handleAdd() {
      if (this.editingRow != -1) return

      this.editingRow = 0
      this.isAdding = true

      this.items = [
        {
          id: '0',
        },
        ...this.items,
      ]
    },

    // Select
    handleSelect(checked, data) {
      const id = data?.item?.id
      if (id == null) return

      this.$set(this.selectedIdsMap, id, checked)
    },
    handleSelectAll() {
      if (this.selectedAll) {
        this.handleUnselectAll()
        return
      }

      this.items.forEach((item) => {
        this.selectedIdsMap = { ...this.selectedIdsMap, [item.id]: true }
      })
    },
    handleUnselectAll() {
      this.selectedIdsMap = {}
    },

    // Edit
    async handleEditStart(data) {
      if (this.selectedIds.length > 0) this.handleUnselectAll()
      if (this.editingRow != -1) return

      this.editingRow = data.index
    },

    handleEditCancel() {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...oldItems] = this.items
        this.items = oldItems

        this.isAdding = false
      }

      this.editingRow = -1
    },

    // Delete
    async handleDelete() {
      if (this.isAddingRates) {
        EventBus.$emit('additionalCost:delete', this.selectedIds)
        this.$refs['mc-modal-delete'].hide()
        this.selectedIdsMap = {}
        this.getData()
        return
      }

      this.isBusy = true
      const res = await this.apiClient.delete({ ids: this.selectedIds })

      if (!res) {
        this.$mcToast.error('Terjadi kesalahan')
        this.isBusy = false
        this.$refs['mc-modal-delete'].hide()
        return
      }

      if (res.errors) {
        this.$mcToast.error('Gagal menghapus data biaya tambahan')
        this.isBusy = false
        this.$refs['mc-modal-delete'].hide()
        return
      }

      this.isBusy = false
      this.$refs['mc-modal-delete'].hide()
      this.$mcToast.success('Data berhasil dihapus')
      this.selectedIdsMap = {}

      this.getData()
    },

    // Submit
    async handleSubmit(data) {
      if (this.isSubmitting) {
        return this.$mcToast.warning('Mohon tunggu')
      }

      this.isSubmitting = true

      // Cast fields to correct types
      const transformedData = this.transformFields(data, this.fields)

      let isSuccess = true
      if (this.isAdding) {
        isSuccess = await this.submitStore(transformedData)
      } else {
        isSuccess = await this.submitUpdate(transformedData, this.items[this.editingRow])
      }

      if (!isSuccess) {
        this.isSubmitting = false
        this.editingRow = -1
        return
      }

      this.getData()

      this.editingRow = -1
      this.isAdding = false
      this.isSubmitting = false
    },
    async submitStore(data) {
      if (this.isAddingRates) {
        const selectedAdditionalCost =
          this.$refs['additional_cost_id-ref']?.$children[0]?.inputValueGetter?.() || {}

        const emitPayload = transformAdditionalCostEmitPayload(
          data,
          selectedAdditionalCost,
          String(this.additionalCosts.length)
        )

        EventBus.$emit('additionalCost:create', emitPayload)
        return true
      }

      const transformed = transformAdditionalCostStoreRequest(data)
      const res = await this.apiClient.store(transformed)

      if (!res) {
        this.$mcToast.error('Terjadi kesalahan')
        return false
      }

      if (res.errors) {
        this.$mcToast.error('Gagal menyimpan data biaya tambahan')
        return false
      }

      return true
    },
    async submitUpdate(data, oldData) {
      if (this.isAddingRates) {
        const selectedAdditionalCost =
          this.$refs['additional_cost_id-ref']?.$children[0]?.inputValueGetter?.() || {}

        const emitPayload = transformAdditionalCostEmitPayload(
          data,
          selectedAdditionalCost,
          this.additionalCosts.length
        )

        EventBus.$emit('additionalCost:update', emitPayload)
        return true
      }

      const diff = transformAdditionalCostUpdateRequest(data, oldData)
      if (isEmpty(diff)) return

      const res = await this.apiClient.update(oldData.id, diff)

      if (!res) {
        this.$mcToast.error('Terjadi kesalahan')
        return false
      }

      if (res.errors) {
        this.$mcToast.error('Gagal menyimpan data biaya tambahan')
        return false
      }

      return true
    },

    // Helpers
    transformCellValue(value) {
      if (value === false) return ''
      if (typeof value === 'number') return value.toLocaleString()

      return value
    },
    isEditing(idx) {
      return this.editingRow == idx
    },
    isSelected(id) {
      return this.selectedIdsMap[id]
    },
    handleFailedValidation(_errors) {
      return this.$mcToast.error('Terdapat kesalahan format pada input anda')
    },
    getInitialValue(item, field) {
      // get value from callback in field setting
      // can be used for formatting select input
      if (field.customValueFn) {
        return field.customValueFn(item) || false
      }

      // get value from item
      if (typeof item[field.key] !== 'undefined') {
        return item[field.key]
      }

      return false
    },
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
